class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.renderRevision = 0;
    this.pendingRefresh = null;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  // AMA 2026-10-25: a successful add must not be reported as failed merely
  // because Shopify could not render a section. Never repeat the add request.
  renderContents(parsedState) {
    this.productId = parsedState?.id;
    try {
      this.replaceCartContents(parsedState?.sections);
    } catch (error) {
      console.warn('[AMA cart] Refreshing incomplete cart sections.', error);
      return this.refreshContents(true, true);
    }
    this.open();
  }

  replaceCartContents(sections) {
    const source = this.getSectionDOM(sections?.['cart-drawer'], '#CartDrawer');
    const target = this.querySelector('#CartDrawer');
    const checkout = source?.querySelector('#CartDrawer-Checkout');
    const form = source?.querySelector('#CartDrawer-Form');
    if (!target || !source?.querySelector('.drawer__inner') || !form || !checkout) {
      throw new Error('Incomplete cart drawer section');
    }

    // Validate first, then replace the whole drawer, including its form and
    // checkout button. Replacing only products/subtotal leaves a stale button.
    const empty = checkout.disabled;
    target.innerHTML = source.innerHTML;
    this.classList.toggle('is-empty', empty);
    this.renderRevision += 1;
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));

    // The badge is optional: its failure must never prevent checkout.
    const badge = document.getElementById('cart-icon-bubble');
    const badgeSource = this.getSectionDOM(sections?.['cart-icon-bubble']);
    if (badge && badgeSource) badge.innerHTML = badgeSource.innerHTML;

    // An already-open drawer has had its focused nodes replaced.
    if (this.classList.contains('active')) {
      const inner = this.querySelector('.drawer__inner');
      trapFocus(inner, inner);
    }
  }

  refreshContents(openAfterRefresh = false, recoverOnFailure = false) {
    this.openAfterRefresh = this.openAfterRefresh || openAfterRefresh;
    this.recoverOnFailure = this.recoverOnFailure || recoverOnFailure;
    if (this.pendingRefresh) return this.pendingRefresh;

    const revision = this.renderRevision;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);
    const url = new URL(routes.cart_url, window.location.origin);
    url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');

    this.pendingRefresh = fetch(url.toString(), {
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error('Cart section refresh failed');
        return response.json();
      })
      .then((sections) => {
        // A newer add/change may already have rendered the correct cart.
        if (revision === this.renderRevision) this.replaceCartContents(sections);
        if (this.openAfterRefresh) this.open();
      })
      .catch((error) => {
        console.warn('[AMA cart] Could not refresh cart sections.', error);
        if (revision === this.renderRevision && this.recoverOnFailure) this.navigateToCart();
      })
      .finally(() => {
        clearTimeout(timeout);
        this.pendingRefresh = null;
        this.openAfterRefresh = false;
        this.recoverOnFailure = false;
      });
    return this.pendingRefresh;
  }

  navigateToCart() {
    // Read-only navigation: the server already owns the cart. No second POST,
    // no duplicate quantity, and no automatic payment or order placement.
    window.location.assign(routes.cart_url);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string' || !html.trim()) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate() {
    // Include the checkout button: the inherited partial refresh omits it.
    return this.closest('cart-drawer')?.refreshContents(false, true);
  }

  updateQuantity(...args) {
    // Invalidate an older background read before a new user change starts.
    const drawer = this.closest('cart-drawer');
    if (drawer) drawer.renderRevision += 1;
    return super.updateQuantity(...args);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
